class DetailsModal extends HTMLElement {
  constructor() {
    super();
    this.detailsContainer = this.querySelector('details');
    this.summaryToggle = this.querySelector('summary');
    this.scrollUnlocked = this.hasAttribute('data-scroll-unlocked');
    this.hasPredictiveSearch = this.hasAttribute('data-has-predictive-search');
    this.predictiveSearch = this.detailsContainer.querySelector('predictive-search');
    this.scrollLocked = false;
    this.focusContainer = this.detailsContainer.querySelector(
      '[data-modal-focus-container]'
    );

    this.detailsContainer.addEventListener(
      'keyup',
      (event) => event.code.toUpperCase() === 'ESCAPE' && this.close()
    );
    this.summaryToggle.addEventListener(
      'click',
      this.onSummaryClick.bind(this)
    );
    this.querySelector('button[type="button"]').addEventListener(
      'click',
      this.onButtonClick.bind(this)
    );

    this.summaryToggle.setAttribute('role', 'button');
  }

  isOpen() {
    return this.detailsContainer.hasAttribute('open');
  }

  onSummaryClick(event) {
    event.preventDefault();
    event.target.closest('details').hasAttribute('open')
      ? this.close()
      : this.open(event);
  }

  onBodyClick(event) {
    if (!this.contains(event.target) || event.target.classList.contains('modal-overlay')) this.close(false);
  }

  onButtonClick(event) {
    event.preventDefault();
    this.close();
  }

  open(event) {
    this.onBodyClickEvent =
      this.onBodyClickEvent || this.onBodyClick.bind(this);
    event.target.closest('details').setAttribute('open', true);
    document.body.addEventListener('click', this.onBodyClickEvent);

    if (!this.scrollUnlocked) {
      this.lockScroll();
    }

    this.scheduleTrapFocus();
  }

  lockScroll() {
    if (this.scrollLocked) {
      return;
    }

    this.scrollPosition = window.scrollY || document.documentElement.scrollTop || 0;
    this.bodyHadOverflowHidden = document.body.classList.contains('overflow-hidden');
    this.htmlHadOverflowHidden = document.documentElement.classList.contains('overflow-hidden');
    this.bodyStyle = {
      position: document.body.style.position,
      top: document.body.style.top,
      left: document.body.style.left,
      right: document.body.style.right,
      width: document.body.style.width,
    };

    document.body.classList.add('overflow-hidden');
    document.documentElement.classList.add('overflow-hidden');
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.scrollPosition}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
    this.scrollLocked = true;
  }

  unlockScroll() {
    if (!this.scrollLocked) {
      return;
    }

    if (!this.bodyHadOverflowHidden) {
      document.body.classList.remove('overflow-hidden');
    }

    if (!this.htmlHadOverflowHidden) {
      document.documentElement.classList.remove('overflow-hidden');
    }

    document.body.style.position = this.bodyStyle.position;
    document.body.style.top = this.bodyStyle.top;
    document.body.style.left = this.bodyStyle.left;
    document.body.style.right = this.bodyStyle.right;
    document.body.style.width = this.bodyStyle.width;
    this.restoreScrollPosition();
    this.scrollLocked = false;
  }

  restoreScrollPosition() {
    const htmlScrollBehavior = document.documentElement.style.scrollBehavior;
    const bodyScrollBehavior = document.body.style.scrollBehavior;

    document.documentElement.style.scrollBehavior = 'auto';
    document.body.style.scrollBehavior = 'auto';
    window.scrollTo(0, this.scrollPosition);

    requestAnimationFrame(() => {
      document.documentElement.style.scrollBehavior = htmlScrollBehavior;
      document.body.style.scrollBehavior = bodyScrollBehavior;
    });
  }

  scheduleTrapFocus() {
    const trapFocus = () => {
      if (this.isOpen()) {
        this.trapFocus();
      }
    };

    trapFocus();
    requestAnimationFrame(trapFocus);
  }

  trapFocus() {
    const focusContainer =
      this.focusContainer || this.detailsContainer.querySelector('[tabindex="-1"]');
    const initialFocusElement = this.getInitialFocusElement() || focusContainer;

    if (!focusContainer) {
      return;
    }

    trapFocus(
      focusContainer,
      initialFocusElement,
      false
    );
    this.focusInitialElement(initialFocusElement);
  }

  getInitialFocusElement() {
    return this.detailsContainer.querySelector('[data-modal-initial-focus]');
  }

  focusInitialElement(initialFocusElement) {
    if (!initialFocusElement) {
      return;
    }

    try {
      initialFocusElement.focus({ preventScroll: true });
    } catch {
      initialFocusElement.focus();
    }

    if (typeof initialFocusElement.setSelectionRange !== 'function') {
      return;
    }

    const valueLength = initialFocusElement.value.length;
    initialFocusElement.setSelectionRange(valueLength, valueLength);
  }

  animatedClose(focusToggle = true) {
    this.predictiveSearch.close();
    this.classList.add('animate-out');

    setTimeout(() => {
      this.classList.remove('animate-out');
      this.closeDefault(focusToggle);
    }, 550);
  }

  close(focusToggle = true) {
    if (this.hasPredictiveSearch && this.predictiveSearch) {
      this.animatedClose(focusToggle);
      return;
    }

    this.closeDefault(focusToggle);
  }

  closeDefault(focusToggle = true) {
    removeTrapFocus(focusToggle ? this.summaryToggle : null);
    this.detailsContainer.removeAttribute('open');
    document.body.removeEventListener('click', this.onBodyClickEvent);

    if (!this.scrollUnlocked) {
      this.unlockScroll();
    }
  }
}

customElements.define('details-modal', DetailsModal);
